/**
 * stepFlow.js — Single source of truth for the booking step flow.
 * Pure functions only. No React, no side effects.
 */
import { SEAT_INTERACTION_MODE } from "../zone-layout/seatInteractionConfig";

export const STEP = {
  VENUE: "venue",
  DATETIME: "datetime",
  SEASON_PASSES: "season_passes",
  ZONE: "zone",
  SEATING: "seating",
  TICKETS: "tickets",
  REVIEW: "review",
};

const ALL_STEPS = [
  { key: STEP.VENUE, label: "Venue" },
  { key: STEP.DATETIME, label: "Date & Time" },
  { key: STEP.SEASON_PASSES, label: "Season Passes" },
  { key: STEP.ZONE, label: "Zone Selection" },
  { key: STEP.SEATING, label: "Seat Selection" },
  { key: STEP.TICKETS, label: "Tickets" },
  { key: STEP.REVIEW, label: "Review" },
];

/**
 * Returns true when the dates object has multiple dates OR
 * any single date has multiple time slots.
 */
export function deriveMultiDateCondition(dates) {
  if (!dates) return false;
  const dateKeys = Object.keys(dates);
  if (dateKeys.length > 1) return true;
  return Object.values(dates).some((d) => {
    const evs = Array.isArray(d) ? d : d.events;
    return (evs?.length ?? 0) > 1;
  });
}

/**
 * The CORE engine for step visibility.
 * Industry approach: Single source of truth for visibility logic.
 */
export function getAvailableSteps(conditions) {
  const mode = conditions.interactionMode || SEAT_INTERACTION_MODE;
  const {
    isGroup,
    needsVenueSelection,
    isMultiDate,
    isZone,
    isSitting,
    haveConfiguredSeats,
    ticketStepActive,
    seasonPassStepActive,
    bookingPath,
  } = conditions;

  return ALL_STEPS.filter(({ key }) => {
    switch (key) {
      case STEP.VENUE:
        return isGroup && needsVenueSelection;

      case STEP.DATETIME:
        return isGroup && isMultiDate;

      case STEP.SEASON_PASSES:
        // Only in the tab strip while the user is browsing season passes.
        return Boolean(seasonPassStepActive);

      case STEP.ZONE:
        if (bookingPath === "season_pass" || seasonPassStepActive) return false;
        return isZone;

      case STEP.SEATING:
        if (bookingPath === "season_pass" || seasonPassStepActive) return false;
        if (isZone && mode === "zoom") return false;
        if (ticketStepActive) return false;
        return isSitting && haveConfiguredSeats;

      case STEP.TICKETS:
        if (bookingPath === "season_pass" || seasonPassStepActive) return false;
        if (ticketStepActive) return true;
        // Show tickets only if NOT sitting OR if seating is not configured for this slot
        return !isSitting || !haveConfiguredSeats;

      case STEP.REVIEW:
        return true;

      default:
        return false;
    }
  });
}

/**
 * Resolves the next logical step.
 */
export function getNextStep(currentStep, availableSteps) {
  const idx = availableSteps.findIndex((s) => s.key === currentStep);
  if (idx === -1 || idx === availableSteps.length - 1) return null;
  return availableSteps[idx + 1].key;
}

export function getPrevStep(currentStep, availableSteps) {
  const idx = availableSteps.findIndex((s) => s.key === currentStep);
  if (idx <= 0) return null;
  return availableSteps[idx - 1].key;
}

export function getInitialStep(conditions) {
  const steps = getAvailableSteps(conditions);
  return steps[0]?.key ?? STEP.TICKETS;
}

/**
 * Debug helper to trace flow decisions.
 */
export function debugFlow(label, conditions, activeStep, availableSteps) {
  if (process.env.NODE_ENV !== "development") return;
}
